// FEAT-002: jointly owned properties.
// casavello NEVER reduces the figures: a joint property is always shown at 100% (that is what you enter in your filing
// software, together with your ownership share, and the software calculates your share). "Your share" is worked out
// here for INFORMATION only, to the penny. Everything on screen and in Excel comes from the functions in this file
// (and the MTD ones in mtdScope.js), so the two always agree. Plain CommonJS so the backend test run can test it.

const JOINT_FILING_NOTE = 'For jointly owned properties, enter the 100% figures and your ownership share in your filing software. It will calculate your share.';
const SHARE_PROMPT = 'Ownership share not set: set it on the property page. The 100% figures are shown in full.';
const SHARE_MIN = 1;
const SHARE_MAX = 99;

// -> { empty, valid, value }  (the server uses the same rule: services/ownershipShare.js)
function validateShare(raw) {
  if (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '')) return { empty: true, valid: false, value: null };
  if (typeof raw !== 'number' && typeof raw !== 'string') return { empty: false, valid: false, value: null };
  const text = String(raw).trim();
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(text)) return { empty: false, valid: false, value: null };
  const n = Number(text);
  if (!(n >= SHARE_MIN && n <= SHARE_MAX)) return { empty: false, valid: false, value: null };
  return { empty: false, valid: true, value: n };
}

// The share of a property record, or null when it is not set (or not a valid share)
function propertyShare(property) {
  if (!property) return null;
  const v = validateShare(property.ownershipSharePercent ?? property.ownership_share_percent);
  return v.valid ? v.value : null;
}

const isJointProperty = (p) => !!p && String(p.ownership || '').toLowerCase() === 'joint';

// to the penny, halves rounded away from zero (so -0.005 becomes -0.01, like 0.005 becomes 0.01)
function roundPence(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  // toPrecision(15) removes binary floating-point noise first (1.255 * 100 is 125.49999999999999), then halves go away from zero
  const r = Math.round(Number((Math.abs(x) * 100).toPrecision(15))) / 100;
  return x < 0 ? -r : r;
}

// your share of an amount, to the penny; null when there is no share
function shareOf(amount, sharePercent) {
  if (sharePercent === null || sharePercent === undefined) return null;
  return roundPence((Number(amount) || 0) * sharePercent / 100);
}

// ---- UK tax report ---------------------------------------------------------------------------------------------
// A tax-report row (one property) -> the joint line: 100% figures plus "your share" for information
function jointTaxLine(row) {
  const sharePercent = propertyShare(row);
  return {
    id: row.id, address: row.address, jurisdiction: row.jurisdiction || 'england', currency: row.currency || 'GBP',
    income: row.income, allowableExpenses: row.allowableExpenses, mortgageInterest: row.mortgageInterest,
    taxableProfit: row.taxableProfit, s24Credit: row.s24Credit,                       // all at 100%
    sharePercent, shareMissing: sharePercent === null,
    yourIncome: shareOf(row.income, sharePercent),
    yourAllowableExpenses: shareOf(row.allowableExpenses, sharePercent),
    yourMortgageInterest: shareOf(row.mortgageInterest, sharePercent),
    yourTaxableProfit: shareOf(row.taxableProfit, sharePercent),
  };
}

// rows -> { main: personal and company rows exactly as before, joint: joint lines }.
// Joint properties are never mixed into the totals of the others.
function splitTaxRows(rows) {
  const main = []; const joint = [];
  for (const r of rows || []) (isJointProperty(r) ? joint : main).push(r);
  return { main, joint: joint.map(jointTaxLine) };
}

function sumTaxRows(rows) {
  const sum = (f) => (rows || []).reduce((s, r) => s + (Number(f(r)) || 0), 0);
  return {
    inc: sum((r) => r.income), allowable: sum((r) => r.allowableExpenses), mortgageInterest: sum((r) => r.mortgageInterest),
    s24Credit: sum((r) => r.s24Credit), taxableProfit: sum((r) => r.taxableProfit),
  };
}

// total of the "your share" figures (information only); lines without a share are left out
function sumJointYourShare(lines) {
  const withShare = (lines || []).filter((l) => !l.shareMissing);
  const sum = (f) => roundPence(withShare.reduce((s, l) => s + f(l), 0));
  return { count: withShare.length, income: sum((l) => l.yourIncome), allowableExpenses: sum((l) => l.yourAllowableExpenses),
    mortgageInterest: sum((l) => l.yourMortgageInterest), taxableProfit: sum((l) => l.yourTaxableProfit) };
}

// money text for the joint blocks: unlike the older exports this keeps the minus sign of a loss
function signedMoney(n, symbol = '£') {
  const x = Number(n);
  if (!Number.isFinite(x)) return `${symbol}0.00`;
  return `${x < 0 ? '-' : ''}${symbol}${Math.abs(x).toFixed(2)}`;
}
const shareLabel = (sharePercent) => (sharePercent === null || sharePercent === undefined ? 'NOT SET' : `${sharePercent.toFixed(2)}%`);

// The joint block of the UK tax Excel (13 columns). `fmt` turns a number into text. The screen shows the same lines.
function jointTaxExcelRows(lines, fmt = signedMoney) {
  const blank = (n) => Array(n).fill('');
  if (!lines || !lines.length) return [];
  const rows = [
    ['JOINTLY OWNED PROPERTIES — 100% FIGURES', ...blank(12)],
    [JOINT_FILING_NOTE, ...blank(12)],
    ['Property', 'Jurisdiction', 'Currency', 'Gross Income (100%)', 'Allowable Expenses (100%)', 'Mortgage Interest (100%)', 'Taxable Profit (100%)',
      'S24 Tax Credit est. (100%)', 'Ownership share %', 'Your share: Income (info only)', 'Your share: Allowable Expenses (info only)',
      'Your share: Mortgage Interest (info only)', 'Your share: Taxable Profit (info only)'],
    ...lines.map((l) => [
      l.address, String(l.jurisdiction).replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()), l.currency,
      fmt(l.income), fmt(l.allowableExpenses), l.mortgageInterest > 0 ? fmt(l.mortgageInterest) : '—', fmt(l.taxableProfit), l.s24Credit > 0 ? fmt(l.s24Credit) : '—',
      shareLabel(l.sharePercent),
      ...(l.shareMissing ? [SHARE_PROMPT, '', '', ''] : [fmt(l.yourIncome), fmt(l.yourAllowableExpenses), l.mortgageInterest > 0 ? fmt(l.yourMortgageInterest) : '—', fmt(l.yourTaxableProfit)]),
    ]),
  ];
  const total = sumJointYourShare(lines);
  if (total.count > 0) {
    rows.push(['Your share total (information only; properties with a share set)', '', '', '', '', '', '', '', '', fmt(total.income), fmt(total.allowableExpenses), fmt(total.mortgageInterest), fmt(total.taxableProfit)]);
  }
  return rows;
}

// BUG-010: the "Personal" choice in a report's Ownership filter means "not owned by a company": personally owned AND
// jointly owned properties. (Joint properties are still shown on their own lines at 100%, never mixed into personal totals.)
// 'all' lets everything through, 'company' only company-owned, 'joint' only joint; a property with no ownership matches
// nothing except 'all', exactly as before.
function ownershipMatches(property, filter) {
  if (!filter || filter === 'all') return true;
  const o = String((property && property.ownership) || '').toLowerCase();
  if (filter === 'personal') return o === 'personal' || o === 'joint';
  return o === filter;
}

module.exports = {
  ownershipMatches,
  JOINT_FILING_NOTE, SHARE_PROMPT, SHARE_MIN, SHARE_MAX,
  validateShare, propertyShare, isJointProperty, roundPence, shareOf, signedMoney, shareLabel,
  jointTaxLine, splitTaxRows, sumTaxRows, sumJointYourShare, jointTaxExcelRows,
};
